import type {Dispatch} from 'react'
import {
  Button,
  type ButtonProps,
  type Key,
  Label,
  type LabelProps,
  ListBox,
  ListBoxItem,
  type ListBoxItemProps,
  type ListBoxProps,
  Popover,
  type PopoverProps,
  Select as RacSelect,
  type SelectProps,
  SelectValue,
  type SelectValueProps,
} from 'react-aria-components'

import ChevronDownSvg from '@/assets/icons/chevron-down.svg?react'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './Select.module.scss'

interface Option<T> {
  icon?: ReactNode
  label: string
  value: T
}

interface Props<T> {
  label: string
  options: Option<T>[]
  selectedItem: T | undefined
  setSelectedItem: Dispatch<T>
  buttonProps?: Omit<ButtonProps, 'children'>
  selectValueProps?: Omit<SelectValueProps<Option<T>>, 'children'>
  popoverProps?: Omit<PopoverProps, 'children'>
  listBoxProps?: Omit<ListBoxProps<Option<T>>, 'children'>
  listBoxItemProps?: Omit<ListBoxItemProps<Option<T>>, 'children' | 'textValue' | 'id'>
  labelProps?: Omit<LabelProps & Omit<ComponentProps<typeof VisuallyHidden>, 'ref'>, 'children'>
  prefix?: ReactNode | string
  postfix?: ReactNode | string
}

function Select<T extends Key = Key>({
  label,
  options,
  selectedItem,
  setSelectedItem,
  buttonProps,
  selectValueProps,
  popoverProps,
  listBoxProps,
  listBoxItemProps,
  labelProps,
  prefix,
  postfix,
  ...props
}: Props<T> & Omit<SelectProps<Option<T>>, 'children' | 'selectedKey' | 'onSelectionChange'>) {
  const isSupportedValues = (q: unknown): q is T => {
    return options.some(option => option.value === q)
  }

  const onSelectionChange = (q: unknown) => {
    if (isSupportedValues(q)) {
      // NOTE: somehow type guard is not working inside of useCallback
      setSelectedItem(q)
    }
  }

  return (
    <RacSelect
      {...props}
      className={clsx(style.Select, props.className)}
      selectedKey={selectedItem}
      onSelectionChange={onSelectionChange}
    >
      <VisuallyHidden
        {...labelProps}
        as={Label}
        className={clsx(style.Label, labelProps?.className)}
      >
        {label}
      </VisuallyHidden>
      <Button {...buttonProps} className={clsx(style.SelectButton, buttonProps?.className)}>
        {prefix}
        <SelectValue
          {...selectValueProps}
          className={clsx(style.Value, selectValueProps?.className)}
        />
        {postfix}
        <span aria-hidden className={style.Chevron}>
          <ChevronDownSvg
            className={style.ChevronIcon}
            width={12}
            height={16}
            viewBox='0 0 24 24'
            aria-hidden
          />
        </span>
      </Button>
      <Popover {...popoverProps} className={clsx(style.SelectList, popoverProps?.className)}>
        <ListBox {...listBoxProps} className={clsx(style.ListBox, listBoxProps?.className)}>
          {options.map(option => (
            <ListBoxItem
              {...listBoxItemProps}
              className={clsx(style.ListBoxItem, listBoxItemProps?.className)}
              key={option.value}
              id={option.value}
              textValue={option.label}
            >
              {option.icon}
              {option.label}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </RacSelect>
  )
}

export default Select
